import { useState, useEffect, useCallback } from 'react';
import { playbackSystem } from '@/audio/PlaybackSystem';
import { useAudioEngine } from './useAudioEngine';
import { useVoiceLineStore } from '@/store/voice-line-store';
import type { Chord, VoiceLine, VoicePart } from '@/types';
import type { SuspensionConnector } from '@/types/suspension-connector';

export interface UsePlaybackReturn {
  isPlaying: boolean;
  playheadPosition: number;
  currentChordId: string | null;
  play: () => void;
  pause: () => void;
  stop: () => void;
  togglePlay: () => void;
  setTempo: (bpm: number) => void;
  tempo: number;
  /** Seek to a specific beat position (GRE-115) */
  seek: (beat: number) => void;
  /** Seek to start (beat 0) */
  seekToStart: () => void;
  /** Seek to end */
  seekToEnd: () => void;
  /** Play a single chord isolated (GRE-117) */
  playChordIsolated: (chord: Chord) => void;
  /** Play only selected chords (GRE-117) */
  playSelectedChords: (chords: Chord[]) => void;
}

interface UsePlaybackOptions {
  voiceLinesActive?: boolean;
  voiceLines?: Record<VoicePart, VoiceLine>;
  /** Suspension connectors for GRE-113 suspension playback */
  suspensionConnectors?: SuspensionConnector[];
}

/**
 * React hook for playback control
 * Accepts chords as parameter instead of reading from store
 */
export function usePlayback(
  chords: Chord[],
  totalBeats: number = 16,
  options: UsePlaybackOptions = {}
): UsePlaybackReturn {
  const { voiceLinesActive = false, voiceLines, suspensionConnectors = [] } = options;
  const { isReady: audioReady, isSamplesLoaded, initialize } = useAudioEngine();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadPosition, setPlayheadPosition] = useState(0);
  const [currentChordId, setCurrentChordId] = useState<string | null>(null);
  const [tempo, setTempoState] = useState(120);

  // Get setPlayingNotes from voice line store
  const setPlayingNotes = useVoiceLineStore((state) => state.setPlayingNotes);

  // Set up playback callbacks
  useEffect(() => {
    // Track timeouts for cleanup to prevent memory leaks
    const timeoutIds: NodeJS.Timeout[] = [];

    playbackSystem.setPlayheadCallback((beat: number) => {
      setPlayheadPosition(beat);
    });

    playbackSystem.setChordTriggerCallback((chordId: string) => {
      setCurrentChordId(chordId);
      // Clear after a short delay for visual feedback
      const timeoutId = setTimeout(() => {
        setCurrentChordId(null);
      }, 200);
      timeoutIds.push(timeoutId);
    });

    // Set up note trigger callback for voice lines
    playbackSystem.setNoteTriggerCallback((noteIds: string[]) => {
      setPlayingNotes(noteIds);
      // Clear after duration (visual feedback)
      const timeoutId = setTimeout(() => {
        setPlayingNotes([]);
      }, 300);
      timeoutIds.push(timeoutId);
    });

    // Handle playback end - sync state when playhead reaches end
    playbackSystem.setPlaybackEndCallback(() => {
      setIsPlaying(false);
      setPlayheadPosition(0);
      setCurrentChordId(null);
      setPlayingNotes([]);
    });

    return () => {
      // Clear all pending timeouts to prevent memory leaks
      timeoutIds.forEach(id => clearTimeout(id));
      playbackSystem.dispose();
    };
  }, [setPlayingNotes]);

  // Update total beats when it changes
  useEffect(() => {
    playbackSystem.setTotalBeats(totalBeats);
  }, [totalBeats]);

  // Schedule progression when chords, voice lines, suspensions, OR audio readiness change.
  // Including isSamplesLoaded ensures the effect re-runs once the sampler finishes loading,
  // so initial schedules that early-returned (instrument null) get re-attempted automatically.
  useEffect(() => {
    if (!isSamplesLoaded) return;
    if (voiceLinesActive && voiceLines) {
      playbackSystem.scheduleVoiceLines(voiceLines);
    } else if (chords.length > 0) {
      if (suspensionConnectors.length > 0) {
        playbackSystem.scheduleProgressionWithSuspensions(chords, suspensionConnectors);
      } else {
        playbackSystem.scheduleProgression(chords);
      }
    }
  }, [chords, voiceLinesActive, voiceLines, suspensionConnectors, isSamplesLoaded]);

  const play = useCallback(async () => {
    if (!audioReady) {
      // Initialize audio on first play attempt
      await initialize();
    }
    playbackSystem.play();
    setIsPlaying(true);
  }, [audioReady, initialize]);

  const pause = useCallback(() => {
    playbackSystem.pause();
    setIsPlaying(false);
  }, []);

  const stop = useCallback(() => {
    playbackSystem.stop();
    setIsPlaying(false);
    setPlayheadPosition(0);
    setCurrentChordId(null);
  }, []);

  const togglePlay = useCallback(async () => {
    if (isPlaying) {
      stop(); // Use stop() instead of pause() for cleaner behavior
    } else {
      // Initialize audio first if needed (must happen before scheduling)
      if (!audioReady) {
        await initialize();
      }
      // Schedule events after audio is ready
      if (voiceLinesActive && voiceLines) {
        playbackSystem.scheduleVoiceLines(voiceLines);
      } else if (chords.length > 0) {
        // Use suspension-aware scheduling when suspensions exist (GRE-113)
        if (suspensionConnectors.length > 0) {
          playbackSystem.scheduleProgressionWithSuspensions(chords, suspensionConnectors);
        } else {
          playbackSystem.scheduleProgression(chords);
        }
      }
      playbackSystem.play();
      setIsPlaying(true);
    }
  }, [isPlaying, stop, chords, voiceLinesActive, voiceLines, suspensionConnectors, audioReady, initialize]);

  const setTempo = useCallback((bpm: number) => {
    playbackSystem.setTempo(bpm);
    setTempoState(bpm);
  }, []);

  // Seek functions (GRE-115)
  const seek = useCallback((beat: number) => {
    playbackSystem.seek(beat);
    setPlayheadPosition(beat);
  }, []);

  const seekToStart = useCallback(() => {
    playbackSystem.seekToStart();
    setPlayheadPosition(0);
  }, []);

  const seekToEnd = useCallback(() => {
    playbackSystem.seekToEnd();
    setPlayheadPosition(totalBeats);
  }, [totalBeats]);

  // GRE-117: Play a single chord isolated
  const playChordIsolated = useCallback(async (chord: Chord) => {
    if (!audioReady) {
      await initialize();
    }
    playbackSystem.playChordIsolated(chord);
  }, [audioReady, initialize]);

  // GRE-117: Play only selected chords
  const playSelectedChords = useCallback(async (selectedChords: Chord[]) => {
    if (selectedChords.length === 0) return;
    if (!audioReady) {
      await initialize();
    }
    playbackSystem.playSelectedChords(selectedChords);
    setIsPlaying(true);
  }, [audioReady, initialize]);

  return {
    isPlaying,
    playheadPosition,
    currentChordId,
    play,
    pause,
    stop,
    togglePlay,
    setTempo,
    tempo,
    seek,
    seekToStart,
    seekToEnd,
    playChordIsolated,
    playSelectedChords,
  };
}
